import {html} from 'lit-html';

import TemplateRegistry from '../template/template-registry';
import {AppExtensions, CartItemProperties, currencyConvert, ThemeCurrency} from '../const';
import ShopifyStore from '../shopify-store';
import Util from '../util';
import {cdnImg} from '../shopify-cdn';
import * as log from 'loglevel';
import {formatMoney} from '../money';
import Translate from '../translate';
import {optionClassMap, optionName} from '../template/template-util';
import {ifDefined} from 'lit-html/directives/if-defined';
import {styleMap} from 'lit-html/directives/style-map';
import {repeat} from 'lit-html/directives/repeat';
import {classMap} from 'lit-html/directives/class-map';
import {live} from 'lit-html/directives/live';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';


const DiscountType = {
    NONE: 0, // (default)
    ABSOLUTE: 1, // (default)
    PERCENTAGE: 2,
    PRICE: 3, // fixed product price
}

class BundleController {

    constructor(onChange) {
        this.onChange = onChange;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render

        this.shopifyStore = ShopifyStore.getInstance();

        this.selected = null;
    }

    init() {

        // add dummy product to option values (to allow discounting)
        this.option.values.unshift({
            value: '0',
            priceVariant: {
                id: 0,
            },
            price: 0
        });

        this._syntheticSlots = [];

        // force hidden property (we store the selected bundle id - restored from cart)
        if(this.option.name[0] !== '_'){
            this.option.name = '_' + this.option.name;
        }
    }

    initUI(){

        this.styles = {};
        if(this.option.params.bundleColor) {
            this.styles['--bundle-primary-color'] = this.option.params.bundleColor;
        }
        if(this.option.params.bundleBgColor){
            this.styles['--bundle-primary-bg'] = this.option.params.bundleBgColor;
        }

        this.bundles = this.option.params.bundles;

        this.calcPrices();

        if(!this.selected && this.bundles.length){
            //  exclude back from cart (we restore based on option.value )
            if(!Util.hasQueryParam(window.location.href, 'item')){
                // select default bundle once
                this.config.productForm.addEventListener('options:rendered', () => {
                    let selectedBundle = this.bundles.find((bundle) => bundle.selected);
                    // select first bundle if no selection
                    this.select(selectedBundle || this.bundles[0]);
                }, {once: true});
            }
        }

    }

    calcPrices() {

        // main product (variant) + options (total) - add-ons (bundle add-ons)
        let mainPrice = (this.config.variant.price / 100) + this.config.price.total;

        // only recalculate if the main price changed
        if(!this._priceProduct || mainPrice !== this._priceProduct || this.config.editorMode){

            this._priceProduct = mainPrice;

            this.bundles.forEach((bundle) => {
                this.calcBundlePrice(bundle);
            });

            return true;
        }

        // not changed
        return false;
    }

    calcBundlePrice(bundle) {

        let total = 0;
        let totalBefore = 0;

        bundle.products.forEach((bundleProduct) => {

            let productPrice = 0;

            let featuredImage = '';

            // main product
            if(bundleProduct.id === '0'){
                // current variant price
                productPrice = this.config.variant.price / 100;
                // update product info
                bundleProduct.title = this.config.product.title;
                // stock status
                bundleProduct.available = this.config.variant.available;

                // add-on options price (added after discount calculation)  INFO: price.variantTotal contains bundle products + add-ons not in bundle (simpler to just exclude all)
                bundleProduct.priceOptions = this.config.price.total - this.config.price.variantTotal;

                // add to total price to include in discount
                if(bundleProduct.priceOptions){
                    productPrice += bundleProduct.priceOptions;
                }

                featuredImage = this.config.product.featured_image;

                // check if current variant has custom image
                if(this.config.variant.featured_image && this.config.variant.featured_image.src){
                    featuredImage = this.config.variant.featured_image.src;
                }

            } else {
                // variantsInCurrency resolves the linked products and they are store in the cache
                if(ShopifyStore.products.hasOwnProperty(bundleProduct.handle + '.js')){
                    let shopifyProduct = ShopifyStore.products[bundleProduct.handle + '.js'];

                    // create variants select if multiple variants
                    if(bundleProduct.variants?.length && !bundleProduct.variantsSelect){
                        bundleProduct.variantsSelect = shopifyProduct.variants
                            // only variants that are in the bundle
                            .filter((productVariant) => bundleProduct.variants.includes(String(productVariant.id)))
                            .map((variant) => {
                                return {
                                    id: String(variant.id),
                                    title: variant.title
                                }
                            });
                    }

                    // find variant
                    let productVariant = shopifyProduct.variants.find((variant) => Number(variant.id) === Number(bundleProduct.variant));
                    // price in current currency
                    if(productVariant){
                        productPrice = productVariant.price / 100; // in full currency

                        featuredImage = shopifyProduct.featured_image;

                        // check if variant has custom image
                        if(productVariant.featured_image && productVariant.featured_image.src){
                            featuredImage = productVariant.featured_image.src;
                        }

                        // update title (required to get right language)
                        if(!bundleProduct.variantsSelect){
                            bundleProduct.title = productVariant.name; // title + variant
                        } else {
                            bundleProduct.title = shopifyProduct.title; // just product title - variant is shown as select
                        }

                        // Detect out-of-stock and mark
                        bundleProduct.available = productVariant.available;
                    } else {
                        // If no variant found, mark as unavailable
                        bundleProduct.available = false;
                    }
                }
            }

            if(featuredImage){
                bundleProduct.img = cdnImg(featuredImage, '200x200');
            }

            // price before discounts
            bundleProduct.priceBefore = productPrice;

            switch(bundleProduct.discountType) {
                case DiscountType.ABSOLUTE:
                    bundleProduct.price = Math.max(0, productPrice - currencyConvert(bundleProduct.discount));
                    break;
                case DiscountType.PERCENTAGE:
                    bundleProduct.price = productPrice * (1 - bundleProduct.discount / 100);
                    break;
                case DiscountType.PRICE:
                    bundleProduct.price = currencyConvert(bundleProduct.discount);
                    // fixed base price add options on top
                    if(bundleProduct.priceOptions){
                        bundleProduct.price += bundleProduct.priceOptions;
                    }
                    break;
                default:
                    bundleProduct.price = productPrice; // already in current currency
            }

            // EDGE CASE: affects separate product options product only
            // main product discount is supported - but as soon as the discount exceeds the main product price - we have to discount the options product
            // when the discount is higher than the options product price - we would need to split the discount (overkill) to keep Shopify function simpler we block it here
            // SUPPORTED: disable separate product options product to calculate single discount on main product
            if(ThemeCurrency.checkout.variantId && bundleProduct.priceOptions){

                // we need to check if the discount is higher than the base product price
                let bundlePrice = bundleProduct.price - bundleProduct.priceOptions;

                if(bundlePrice < 0){
                    let totalDiscount = (productPrice - bundleProduct.priceOptions) + Math.abs(bundlePrice);
                    // if the total discount is higher than options - we set the main product to 1 and do NOT apply the full discount
                    if(totalDiscount > bundleProduct.priceOptions){
                        bundleProduct.price = bundleProduct.priceOptions + 1; // options are subtracted from the main product price again
                        log.warn('[live-options] Bundle discount exceeds base product and add-on price', bundle.title);
                        bundle._reset = true; // mark bundle as reset (to update price)
                    }
                }
                // IMPORTANT: if already selected, to charge the right price - update the price again (only required because the discount is reduced based on other selected product options)
                // INFO: total on product page controlled by LPO could still be wrong (no change event)
                if(bundle._reset && this.selected === bundle && this.option.values[0].bundle) {
                    this.option.values[0].bundle.price = bundleProduct.price - bundleProduct.priceOptions;
                }
            }

            total += bundleProduct.price * bundleProduct.quantity;
            totalBefore += bundleProduct.priceBefore * bundleProduct.quantity;
        });

        bundle.price = total;
        bundle.priceBefore = totalBefore;
        bundle.priceDiscount = totalBefore - total;

        let discountText = formatMoney(bundle.priceDiscount * 100, ThemeCurrency.moneyFormat);

        // replace variables (store template in with _ prefix to apply updated values)
        bundle._title = bundle._title || Translate.translate(bundle.title.replace(/{{ /g, '{{').replace(/ }}/g, '}}'))
        bundle.title = bundle._title
            .replace(/{{product}}/g, this.config.product.title)
            .replace(/{{discount}}/g, discountText);
        bundle._subtitle = bundle._subtitle || Translate.translate(bundle.subtitle.replace(/{{ /g, '{{').replace(/ }}/g, '}}'))
        bundle.subtitle = bundle._subtitle.replace(/{{ /g, '{{').replace(/ }}/g, '}}')
            .replace(/{{product}}/g, this.config.product.title)
            .replace(/{{discount}}/g, discountText);

        if(bundle.badge){
            bundle._badge = bundle._badge || Translate.translate(bundle.badge.replace(/{{ /g, '{{').replace(/ }}/g, '}}'))
            bundle.badge = bundle._badge
                .replace(/{{product}}/g, this.config.product.title)
                .replace(/{{discount}}/g, discountText);
        }
    }

    // called in product-options evaluation
    isSelected(option, optionValue)  {
        // value contains check
        if(option.value && option.value.indexOf(optionValue.value) !== -1){
            // NEW: we also have to split to check for the exact value match
            let values = option.value.split(',');
            if(values.indexOf(optionValue.value) !== -1){
                return true;
            }
        }

        return false;
    }

    select(bundle) {

        // store choice (currently only used to detect if already a selection is made)
        this.selected = bundle;

        // deselect all bundles
        this.bundles.forEach((b) => {
            b.selected = false;
        });
        // select this one
        bundle.selected = true;

        let ids = [];

        // all products have to be in stock for the bundle to work
        let inStock = true;

        // detect duplicate variants in this bundle (e.g. same product added twice)
        const variants = bundle.products.filter(p => p.id !== '0').map(p => p.variant);
        const hasDuplicates = new Set(variants).size !== variants.length;

        // only needed when duplicates exist — tracks which option value slots are already taken
        const claimed = hasDuplicates ? new Set() : null;

        if (hasDuplicates) {
            for (const slot of this._syntheticSlots) {
                slot.bundle = null;
                slot.price = 0;
                slot.priceVariant = null;
            }
        }

        for (let i = 0; i < bundle.products.length; i++) {
            const bundleProduct = bundle.products[i];

            // check if product is out of stock > avoid selection
            if(bundleProduct.available === false){
                ids.length = 0; // disable all products
                inStock = false;
                break;
            }

            let optionValue;

            // main product
            if(bundleProduct.id === '0'){
                // first option value is the main product
                optionValue = this.option.values[0];

                // only if main product is discounted OR if more than one 1x (buy 2 get 1 free)
                if(bundleProduct.discount || bundleProduct.quantity > 1){
                    optionValue.bundle = {
                        id: bundleProduct.id,
                        quantity: bundleProduct.quantity,
                        // already calculated discounted price  IMPORTANT: exclude options price (cart transform handles it separately)
                        price: bundleProduct.price - bundleProduct.priceOptions,
                    };

                    // select to update the UI
                    ids.push('0');
                }

                continue;
            }

            if (!hasDuplicates) {
                // original path — no duplicates, simple find
                optionValue = this.option.values.find((ov) => String(bundleProduct.variant) === ov.priceVariant?.id);
            } else {
                // find an unclaimed option value matching this variant
                optionValue = null;
                for (let k = 0; k < this.option.values.length; k++) {
                    const ov = this.option.values[k];
                    if (String(bundleProduct.variant) === ov.priceVariant?.id && !claimed.has(k)) {
                        optionValue = ov;
                        claimed.add(k);
                        break;
                    }
                }

                // all matching slots claimed — use synthetic slot
                if (!optionValue) {
                    for (const slot of this._syntheticSlots) {
                        const idx = this.option.values.indexOf(slot);
                        if (idx !== -1 && !claimed.has(idx)) {
                            optionValue = slot;
                            claimed.add(idx);
                            break;
                        }
                    }

                    if (!optionValue) {
                        optionValue = {
                            value: '_bundle_slot_' + this._syntheticSlots.length,
                            price: 0,
                            priceVariant: { id: String(bundleProduct.variant) },
                        };
                        this.option.values.push(optionValue);
                        this._syntheticSlots.push(optionValue);
                    } else {
                        optionValue.priceVariant = { id: String(bundleProduct.variant) };
                    }
                }
            }

            if(optionValue){
                // use slot value for synthetic slots (unique key), variant id for normal
                ids.push(hasDuplicates ? optionValue.value : bundleProduct.variant);

                // store as bundle on cart
                optionValue.bundle = {
                    id: String(bundleProduct.variant),
                    handle: bundleProduct.handle, // used to lookup product in cart and render bundle layout
                    quantity: bundleProduct.quantity,
                    price: bundleProduct.price, // already calculated discounted price
                };
                // IMPORTANT: set current price for LPO total product price to be calculated correctly
                optionValue.price = bundleProduct.price * bundleProduct.quantity;
            }
        }

        // multi select all variants
        this.option.value = ids.join(',');

        this.onChange(this.option);
    }

    selectVariant(bundle, bundleProduct, variantId) {
        bundleProduct.variant = String(variantId);

        // update prices for this bundle
        this.calcBundlePrice(bundle);

        // always already selected - but we update the price as well on the option and trigger a change
        this.select(bundle);
    }

    template() {

        // restore from cart (option.value stores bundle.id)
        if(this.option.value && !this.selected){
            let selectedBundle = this.bundles.find((bundle) => this.option.value === bundle.id);
            if(selectedBundle){
                // apply visual state synchronously so the current render shows the correct selection
                selectedBundle.selected = true;
                this.selected = selectedBundle;
                // defer full select (with onChange) to after this render cycle completes to avoid nested render
                queueMicrotask(() => this.select(selectedBundle));
            }
        }

        //  recalculate bundle prices (required for the total and variant price to be correct) - only executed if the price changed
        let changed = this.calcPrices();

        // HACKY: if the price changed - we need to re select the bundle to make sure the right prices are saved (causes two renders)
        // INFO: chicken egg problem - we need to select the bundle to get the right price - but we need the right price to select the bundle
        if(!this.config.editorMode && changed && this.selected){
            queueMicrotask(() => this.select(this.selected));
        }

        const option = this.option;
        const config = this.config;

        return html`
        <input type="hidden" name="${optionName(option)}" id="${option.id}" value=${this.selected?.id} form=${ifDefined(config.productFormLink)} />
        <input type="hidden" name="properties[${CartItemProperties.BUNDLE_TITLE}]" value=${this.selected?.title} form=${ifDefined(config.productFormLink)}>

        <div class="cl-bundle-group" style="${styleMap(this.styles)}">
            ${repeat(this.bundles || [], (bundle) => bundle.id, (bundle, bundleIndex) => html`
                <div class="cl-bundle-option ${classMap({ 'cl-bundle-option--selected': bundle.selected })}">
                    <label for="${option.id}-${bundleIndex}" class="cl-bundle-container ${bundle.badge ? 'cl-bundle-container--featured' : ''}">
                        ${bundle.badge ? html`<div class="cl-bundle-badge">${bundle.badge}</div>` : ''}

                        <div class="cl-bundle-wrapper">
                            <div class="cl-bundle-main">
                                <input type="radio" 
                                       class="cl-bundle-input ${classMap(optionClassMap(option))}" 
                                       id="${option.id}-${bundleIndex}" 
                                       name="_${option.name}"
                                       .value=${bundleIndex} 
                                       @change=${() => this.select(bundle)}
                                       .checked=${live(bundle.selected)} 
                                       form=${ifDefined(config.productFormLink)}>

                                <div class="cl-bundle-content">
                                    <div class="cl-bundle-text">
                                        <div class="cl-bundle-title">${unsafeHTML(bundle.title)}</div>
                                        ${bundle.subtitle ? html`<div class="cl-bundle-subtitle">${unsafeHTML(bundle.subtitle)}</div>` : ''}
                                    </div>
                                    <div class="cl-bundle-price">
                                        <span class="cl-bundle-price__current">${unsafeHTML(formatMoney(bundle.price * 100, ThemeCurrency.moneyFormat))}</span>
                                        ${bundle.priceBefore !== bundle.price ? html`
                                            <span class="cl-bundle-price__before">${unsafeHTML(formatMoney(bundle.priceBefore * 100, ThemeCurrency.moneyFormat))}</span>
                                        ` : ''}
                                    </div>
                                </div>
                            </div>

                            ${bundle.products.length > 1 ? html`
                                <div class="cl-bundle-items">
                                    ${repeat(bundle.products, (bundleProduct) => bundleProduct.id, (bundleProduct, productIndex) => html`
                                        <div class="cl-bundle-item${bundleProduct.available === false ? ' cl-bundle-item__unavailable' : ''}">
                                            <div class="cl-bundle-item__wrapper">
                                                <img class="cl-bundle-item__image" src="${bundleProduct.img}" alt="${bundleProduct.title}">
                                                <div class="cl-bundle-item__title">
                                                    ${bundleProduct.quantity > 1 ? html`<span class="cl-bundle-item__quantity">${bundleProduct.quantity}x</span>` : ''}
                                                    <span class="cl-bundle-item__title-text">${bundleProduct.title}</span>
                                                    ${bundle.selected && bundleProduct.variantsSelect?.length ? html`
                                                        <select class="cl-bundle-item__variants" name="_${bundleProduct.id}" @change=${(e) => this.selectVariant(bundle, bundleProduct, e.target.value)}>
                                                            ${repeat(bundleProduct.variantsSelect, (variant) => variant.id, (variant) => html`
                                                                <option value="${variant.id}" ?selected=${variant.id === bundleProduct.variant}>${variant.title}</option>
                                                            `)}
                                                        </select>
                                                    ` : ''}
                                                </div>
                                            </div>
                                            <div class="cl-bundle-price cl-bundle-item-price">
                                                <span class="cl-bundle-price__current">${bundleProduct.price > 0 ? unsafeHTML(formatMoney((bundleProduct.price * 100) * bundleProduct.quantity, ThemeCurrency.moneyFormat)) : Translate.get('product.options.free', {}, 'FREE')}</span>
                                                ${bundleProduct.priceBefore !== bundleProduct.price ? html`
                                                    <span class="cl-bundle-price__before">${unsafeHTML(formatMoney((bundleProduct.priceBefore * 100) * bundleProduct.quantity, ThemeCurrency.moneyFormat))}</span>
                                                ` : ''}
                                            </div>
                                        </div>
                                    `)}
                                </div>
                            ` : ''}
                        </div>
                    </label>
                </div>
            `)}
        </div>
        `;
    }
}

TemplateRegistry.templates['bundle'] = (option, config, onChange, onErrorClear, getOption) => {

    // extension not enabled
    if(!AppExtensions.BUNDLE){
        return html``;
    }

    let init = option._controller === undefined;

    // get or create layer customer controller
    option._controller = option._controller || new BundleController(onChange, getOption);

    // option reference can change (mainly in editor mode) so we just always update it to the current reference
    option._controller.option = option;
    // config is always a new object - storing reference on controller init would keep the old instance
    option._controller.config = config;

    if (init) {
        option._controller.init();
    }
    // editor mode always update call to show updated ui
    if (init || config.editorMode) {
        option._controller.initUI();
    }

    return option._controller.template();
}
